"use client"

import { useState, useEffect, useCallback, useMemo } from "react"
import { X, Upload, Check, Sparkles } from "lucide-react"
import { GlassSidePanel } from "@/src/components/ui/glass-side-panel"
import { Z } from "@/lib/z-index"
import {
  ABILITY_TYPE_CONFIG,
  type AbilityType,
  type PublishDraft,
} from "@/lib/community/types"
import { COMMUNITY_BOARDS } from "@/lib/community/boards"
import { publishPost } from "@/lib/community/community-api"
import { AbilityTypeBadge } from "@/src/components/community/ability-type-badge"
import type { AgentDemoStep } from "@/lib/agent-demo-data"
import {
  extractSkills,
  extractAgents,
  extractTools,
  extractWorkflowGraph,
  generateShareText,
} from "@/lib/community/publish-helpers"
import { PublishSkillPicker } from "./publish-sections/publish-skill-picker"
import { PublishToolPicker } from "./publish-sections/publish-tool-picker"
import { PublishAgentPicker } from "./publish-sections/publish-agent-picker"
import { PublishWorkflowView } from "./publish-sections/publish-workflow-view"
import { PublishTeamView } from "./publish-sections/publish-team-view"

interface ConversationPublishPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  scenarioId?: string
  scenarioTitle?: string
  scenarioDescription?: string
  initialQuery?: string
  demoSteps: AgentDemoStep[]
}

/** Auto-detect board from scenario title keywords */
function detectBoard(scenarioTitle: string): string {
  if (scenarioTitle.includes("炒股") || scenarioTitle.includes("投资"))
    return "quant-trading"
  if (scenarioTitle.includes("自媒体")) return "creative-lab"
  if (scenarioTitle.includes("电商")) return "marketing-hq"
  if (scenarioTitle.includes("合规") || scenarioTitle.includes("飞书"))
    return "efficiency-dojo"
  return "code-planet"
}

/** Auto-detect tags from scenario title keywords */
function detectTags(scenarioTitle: string, abilityType: AbilityType): string[] {
  const tags: string[] = []
  if (scenarioTitle.includes("自媒体")) tags.push("自媒体", "内容创作")
  else if (scenarioTitle.includes("飞书")) tags.push("飞书", "协同办公")
  else if (scenarioTitle.includes("电商")) tags.push("电商", "运营")
  else if (scenarioTitle.includes("合规")) tags.push("合规", "法务")
  else if (scenarioTitle.includes("炒股")) tags.push("量化", "投资")
  else tags.push("AI", "自动化")

  if (abilityType === "agent-team") tags.push("多Agent协作")
  else if (abilityType === "workflow") tags.push("工作流")
  else if (abilityType === "skill") tags.push("Skills")
  else if (abilityType === "tool") tags.push("工具")

  return tags.slice(0, 5)
}

/**
 * ConversationPublishPanel — right-side GlassSidePanel for publishing
 * a conversation as a community post. Content adapts per ability type.
 */
export function ConversationPublishPanel({
  open,
  onOpenChange,
  scenarioId,
  scenarioTitle = "",
  scenarioDescription = "",
  initialQuery = "",
  demoSteps,
}: ConversationPublishPanelProps) {
  // ── Extracted data from demo ──
  const skills = useMemo(() => extractSkills(scenarioId), [scenarioId])
  const agents = useMemo(() => extractAgents(demoSteps), [demoSteps])
  const tools = useMemo(() => extractTools(demoSteps), [demoSteps])
  const workflowNodes = useMemo(() => extractWorkflowGraph(demoSteps), [demoSteps])

  // ── Form state ──
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [abilityType, setAbilityType] = useState<AbilityType>("workflow")
  const [boardSlug, setBoardSlug] = useState("")
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState("")
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  // ── Initialize on open ──
  useEffect(() => {
    if (!open) return

    // Determine default ability type from scenario data
    const defaultType: AbilityType =
      agents.length > 2
        ? "agent-team"
        : agents.length > 0
          ? "workflow"
          : skills.length > 0
            ? "skill"
            : "agent"

    setAbilityType(defaultType)
    setSelectedIds(new Set())
    setSubmitted(false)
    setBoardSlug(detectBoard(scenarioTitle))
    setTags(detectTags(scenarioTitle, defaultType))

    // Generate initial text
    const text = generateShareText(defaultType, [], scenarioTitle, {
      skills,
      agents,
      tools,
      workflowNodes,
    })
    setTitle(text.title)
    setDescription(text.description)
  }, [open, scenarioTitle, scenarioId, skills, agents, tools, workflowNodes])

  // ── Update text when ability type or selection changes ──
  const updateText = useCallback(
    (type: AbilityType, ids: Set<string>) => {
      const text = generateShareText(type, Array.from(ids), scenarioTitle, {
        skills,
        agents,
        tools,
        workflowNodes,
      })
      setTitle(text.title)
      setDescription(text.description)
      setTags(detectTags(scenarioTitle, type))
    },
    [scenarioTitle, skills, agents, tools, workflowNodes]
  )

  const handleTypeChange = useCallback(
    (type: AbilityType) => {
      setAbilityType(type)
      setSelectedIds(new Set())
      updateText(type, new Set())
    },
    [updateText]
  )

  const handleToggleId = useCallback(
    (id: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        updateText(abilityType, next)
        return next
      })
    },
    [abilityType, updateText]
  )

  const handleAddTag = useCallback(() => {
    const trimmed = tagInput.trim()
    if (trimmed && tags.length < 5 && !tags.includes(trimmed)) {
      setTags((prev) => [...prev, trimmed])
      setTagInput("")
    }
  }, [tagInput, tags])

  const handleRemoveTag = useCallback((tag: string) => {
    setTags((prev) => prev.filter((t) => t !== tag))
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!title.trim() || !description.trim() || !boardSlug) return
    setSubmitting(true)
    try {
      const draft: PublishDraft = {
        title: title.trim(),
        description: description.trim(),
        abilityType,
        boardSlug,
        tags,
        shareMode: "showcase",
      }
      await publishPost(draft)
      setSubmitted(true)
      setTimeout(() => onOpenChange(false), 2500)
    } finally {
      setSubmitting(false)
    }
  }, [title, description, abilityType, boardSlug, tags, onOpenChange])

  const canSubmit =
    title.trim().length > 0 && description.trim().length > 0 && boardSlug

  // ── Render type-specific content section ──
  const renderTypeSection = () => {
    switch (abilityType) {
      case "skill":
        return (
          <PublishSkillPicker
            skills={skills}
            selectedIds={selectedIds}
            onToggle={handleToggleId}
            scenarioTitle={scenarioTitle}
          />
        )
      case "tool":
        return (
          <PublishToolPicker
            tools={tools}
            selectedIds={selectedIds}
            onToggle={handleToggleId}
            scenarioTitle={scenarioTitle}
          />
        )
      case "agent":
        return (
          <PublishAgentPicker
            agents={agents}
            selectedIds={selectedIds}
            onToggle={handleToggleId}
            scenarioTitle={scenarioTitle}
          />
        )
      case "workflow":
        return (
          <PublishWorkflowView
            nodes={workflowNodes}
            scenarioTitle={scenarioTitle}
          />
        )
      case "agent-team":
        return (
          <PublishTeamView
            agents={agents}
            workflowNodes={workflowNodes}
            scenarioTitle={scenarioTitle}
          />
        )
      default:
        return null
    }
  }

  return (
    <GlassSidePanel
      side="right"
      open={open}
      onOpenChange={onOpenChange}
      capsuleLabel="发布"
      showCapsule={false}
      layout="full"
      topOffset="4.5rem"
      margin={1}
      maxWidth="max-w-md"
      panelStyleClass="agent-sidebar-panel"
      zIndex={Z.NAV_PILL}
    >
      <div className="flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/30 shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-emerald-500" />
            <h2 className="text-sm font-semibold text-foreground">
              {submitted ? "发布成功" : "总结并发布到社区"}
            </h2>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {submitted ? (
          /* ── Success state ── */
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400">
              <Check size={32} strokeWidth={2.5} />
            </div>
            <p className="text-base font-semibold text-foreground">
              发布成功！
            </p>
            <p className="text-xs text-muted-foreground text-center max-w-[200px]">
              你的内容已分享到 Openclaw 社区，其他用户可以一键复制使用
            </p>
          </div>
        ) : (
          <>
            {/* ── Scrollable form ── */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 no-scrollbar">
              {/* Ability type selector */}
              <div>
                <label className="block text-caption font-medium text-muted-foreground mb-1.5">
                  分享类型
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {(Object.keys(ABILITY_TYPE_CONFIG) as AbilityType[]).map(
                    (type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => handleTypeChange(type)}
                        className={`px-2 py-1 rounded-full text-caption font-medium transition-all ${
                          abilityType === type
                            ? `${ABILITY_TYPE_CONFIG[type].bgClass} ${ABILITY_TYPE_CONFIG[type].textClass} ring-1 ring-current/20`
                            : "bg-secondary/50 text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {ABILITY_TYPE_CONFIG[type].label}
                      </button>
                    )
                  )}
                </div>
              </div>

              {/* ── Type-specific content section ── */}
              <div className="rounded-xl border border-border/20 bg-secondary/10 p-3">
                {renderTypeSection()}
              </div>

              {/* Title */}
              <div>
                <label className="block text-caption font-medium text-muted-foreground mb-1">
                  标题
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={100}
                  className="w-full bg-secondary/30 border border-border/40 rounded-lg px-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-emerald-500/50 transition-colors"
                />
                <span className="text-micro text-muted-foreground mt-0.5 block text-right">
                  {title.length}/100
                </span>
              </div>

              {/* Description */}
              <div>
                <label className="block text-caption font-medium text-muted-foreground mb-1">
                  描述
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  className="w-full bg-secondary/30 border border-border/40 rounded-lg px-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-emerald-500/50 transition-colors resize-none"
                />
                <span className="text-micro text-muted-foreground mt-0.5 block text-right">
                  {description.length}/2000
                </span>
              </div>

              {/* Board */}
              <div>
                <label className="block text-caption font-medium text-muted-foreground mb-1">
                  发布版块
                </label>
                <select
                  value={boardSlug}
                  onChange={(e) => setBoardSlug(e.target.value)}
                  className="w-full bg-secondary/30 border border-border/40 rounded-lg px-3 py-1.5 text-xs text-foreground focus:outline-none focus:border-emerald-500/50 transition-colors"
                >
                  <option value="">选择版块…</option>
                  {COMMUNITY_BOARDS.map((board) => (
                    <option key={board.slug} value={board.slug}>
                      {board.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-caption font-medium text-muted-foreground mb-1">
                  标签（最多 5 个）
                </label>
                <div className="flex flex-wrap gap-1 mb-1.5">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-0.5 text-caption bg-secondary/70 text-foreground/80 px-1.5 py-0.5 rounded-full"
                    >
                      #{tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
                {tags.length < 5 && (
                  <div className="flex gap-1.5">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault()
                          handleAddTag()
                        }
                      }}
                      placeholder="输入标签按 Enter"
                      className="flex-1 bg-secondary/30 border border-border/40 rounded-lg px-2.5 py-1 text-caption text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-emerald-500/50 transition-colors"
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="px-2.5 py-1 text-caption text-muted-foreground hover:text-foreground bg-secondary/50 rounded-lg transition-colors"
                    >
                      添加
                    </button>
                  </div>
                )}
              </div>

              {/* Preview card */}
              <div>
                <label className="block text-caption font-medium text-muted-foreground mb-1">
                  预览
                </label>
                <div className="liquid-glass-card rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <AbilityTypeBadge type={abilityType} size="sm" />
                    {boardSlug && (
                      <span className="text-micro text-muted-foreground">
                        {COMMUNITY_BOARDS.find((b) => b.slug === boardSlug)
                          ?.name}
                      </span>
                    )}
                  </div>
                  <h3 className="text-xs font-semibold text-foreground mb-1 truncate">
                    {title || "（无标题）"}
                  </h3>
                  <p className="text-caption text-foreground/70 line-clamp-3 whitespace-pre-line leading-relaxed">
                    {description || "（无描述）"}
                  </p>
                  {tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {tags.map((tag) => (
                        <span
                          key={tag}
                          className="text-micro bg-secondary/70 text-foreground/70 px-1.5 py-0.5 rounded-full"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer — publish button */}
            <div className="px-4 py-3 border-t border-border/30 shrink-0">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                className="w-full flex items-center justify-center gap-2 h-9 rounded-xl text-xs font-medium bg-emerald-500 text-white hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? (
                  <>
                    <div className="h-3.5 w-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    发布中…
                  </>
                ) : (
                  <>
                    <Upload className="h-3.5 w-3.5" />
                    发布到社区 🐾
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </GlassSidePanel>
  )
}
